import { message } from "antd";
import axiosInstance from "../../../helpers/axios";
import { decryptByText, responseDataFormate } from "../../../helpers/bycrypt";
import { CapitalizeFirstWord } from "../../../helpers/utils";
export interface QuotationFilterParams {
  page?: number;
  limit?: number;
}

interface GetQuotationListParams {
  filters: QuotationFilterParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getApprovedQuotationsListAPI =
  ({ filters, onSuccess, onError }: GetQuotationListParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get("/quotes/approved/list", {
        params: filters,
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Quotation List Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

interface CreateOrderParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
  onAddressIncompleteError: (data: any) => void;
  onDrawingRequiredError: (data: any) => void;
  onCustomerOnHoldError?: (data: any) => void;
}

export const createOrderAPI =
  ({
    payload,
    onSuccess,
    onError,
    onAddressIncompleteError,
    onDrawingRequiredError,
    onCustomerOnHoldError,
  }: CreateOrderParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(`/orders`, payload);

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      onError?.();

      const status = error?.response?.status;
      const errorMessage = error?.response?.data?.message;
      if (status === 422) {
        if (onAddressIncompleteError) {
          onAddressIncompleteError(error?.response?.data);
        }
      } else if (status === 428) {
        if (onDrawingRequiredError) {
          onDrawingRequiredError(error?.response?.data);
        }
      } else if (status === 435) {
        // Handle on hold customer error
        if (onCustomerOnHoldError) {
          onCustomerOnHoldError(error?.response?.data);
        }
      } else {
        // Handle all other errors (500, 400, etc.)
        message.error(errorMessage || "Something went wrong");
      }
    }
  };

export interface OrderFilterParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  customer_id?: string[];
  created_by?: string[];
  start_date?: string;
  end_date?: string;
  sortBy?: string | null;
  sortOrder?: "asc" | "desc" | null;
  is_active?: number;
}

export const fetchOrdersQueryFn = async (
  filters: any,
  signal?: AbortSignal,
) => {
  const response = await axiosInstance.get("/orders", {
    params: filters,
    signal,
  });

  const result = responseDataFormate(response.data);

  if (result?.success) {
    const formattedOrders = result?.data?.orders?.map((order: any) => ({
      ...order,
      order_created_by: order?.order_created_by
        ? {
            ...order?.order_created_by,
            email: decryptByText(order?.order_created_by?.email),
            first_name: CapitalizeFirstWord(
              decryptByText(order?.order_created_by?.first_name),
            ),
            last_name: CapitalizeFirstWord(
              decryptByText(order?.order_created_by?.last_name),
            ),
          }
        : order?.created_by,
    }));
    return { ...result.data, orders: formattedOrders };
  } else {
    throw new Error(result?.message || "Failed to fetch Orders");
  }
};

interface ResolveTBCVariantsAPIParams {
  order_id: string;
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const resolveTBCVariantsAPI =
  ({ order_id, payload, onSuccess, onError }: ResolveTBCVariantsAPIParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/orders/${order_id}/resolve-drop-tbc`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Resolve TBC variants error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

export const confirmVariableWarningsAPI =
  (
    order_id: string,
    resolvedItems: any[],
    onSuccess: () => void,
    onError: () => void,
  ) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/orders/${order_id}/confirm-variable-warnings`,
        { resolvedItems },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.();
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Confirm Variable Warnings error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage || "Something went wrong");
    }
  };

interface ToggleArchiveOrderParams {
  order_id: string;
  is_archived: boolean;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const toggleArchiveOrderAPI =
  ({ order_id, is_archived, onSuccess, onError }: ToggleArchiveOrderParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(`/orders/archive`, {
        order_id,
        is_archived,
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Toggle Archive Order Error:", error);
      onError?.();
      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage || "Something went wrong");
    }
  };

export const getPossibleBomDimensionsAPI =
  (order_id: string, onSuccess: (data: any) => void, onError?: () => void) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        `/orders/${order_id}/possible-bom-dimensions`,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Possible BOM Dimensions Error:", error);
      onError?.();
    }
  };

export interface SingleBomDimensionParams {
  order_id: string;
  type: "part" | "component";
  part_id?: string;
  component_id?: string;
  bom_variant_id?: string;
  bom_id?: string;
  dimension?: string;
}

export const getSinglePossibleBomDimensionsAPI =
  (
    params: SingleBomDimensionParams,
    onSuccess: (data: any) => void,
    onError?: () => void,
  ) =>
  async (dispatch: any) => {
    try {
      const { order_id, ...queryParams } = params;
      const response = await axiosInstance.get(
        `/orders/${order_id}/possible-bom-dimensions/single`,
        { params: queryParams },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Single Possible BOM Dimensions Error:", error);
      onError?.();
    }
  };

interface UpdateOrderChecklistParams {
  order_id: string;
  payload: {
    checklist_answers: any[];
    force_update?: boolean;
  };
  onSuccess?: (data: any) => void;
  onError?: () => void;
  onWarnings?: (warnings: any[], severity?: string) => void;
}

export const updateOrderChecklistAPI =
  ({
    order_id,
    payload,
    onSuccess,
    onError,
    onWarnings,
  }: UpdateOrderChecklistParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/orders/${order_id}/installation-checklist`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (response.status === 207) {
        onWarnings?.(
          result.data?.warnings || [],
          result.severity || result.data?.severity || "",
        );
        return;
      }

      if (result?.success) {
        message.success(
          result?.message || "Installation checklist updated successfully",
        );
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Order Installation Checklist Error:", error);
      onError?.();
      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage || "Failed to update installation checklist");
    }
  };

interface RegenerateOrderBomParams {
  order_id: string;
  is_2bs_order?: boolean;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const regenerateOrderBomAPI =
  ({ order_id, is_2bs_order, onSuccess, onError }: RegenerateOrderBomParams) =>
  async (dispatch: any) => {
    try {
      const endpoint = is_2bs_order
        ? `/2bs-order/${order_id}/regenerate-bom`
        : `/orders/${order_id}/regenerate-bom`;
      const response = await axiosInstance.post(endpoint);
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(
          result?.message ||
            (is_2bs_order
              ? "2BS Order BOM regenerated successfully"
              : "Order BOM regenerated successfully"),
        );
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to regenerate BOM");
        onError?.();
      }
    } catch (error: any) {
      console.error("Regenerate Order BOM Error:", error);
      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage || "Failed to regenerate BOM");
      onError?.();
    }
  };

export const regenerate2BSOrderBomAPI =
  ({ order_id, onSuccess, onError }: RegenerateOrderBomParams) =>
  regenerateOrderBomAPI({ order_id, is_2bs_order: true, onSuccess, onError });
  
export const updateOrderAccountsApprovalAPI =
  ({
    order_id,
    onSuccess,
    onError,
  }: {
    order_id: string;
    onSuccess?: (data: any) => void;
    onError?: () => void;
  }) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/orders/${order_id}/accounts-approval`,
        {},
      );
      let result = responseDataFormate(response.data);
      if (!result && response?.data) {
        result = response.data;
      }

      if (result?.success) {
        message.success(result?.message || "Order marked as accounts approved");
        onSuccess?.(result.data || { _id: order_id });
      } else {
        message.error(result?.message || "Failed to approve order");
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Accounts Approval Error:", error);
      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage || "Failed to approve order");
      onError?.();
    }
  };

